import React, { useState, useEffect, useRef, useCallback } from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import chemicalsByCategory from './chemicaldata';
import './product.css';

const Products = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [selectedCategory, setSelectedCategory] = useState("Pre-Treatment");
    const [expandedChemicalName, setExpandedChemicalName] = useState(null); // Track the name of the expanded chemical

    const categoriesRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const updateArrows = useCallback(() => {
        const el = categoriesRef.current;
        if (!el) return;
        const overflows = el.scrollWidth > el.clientWidth + 1;
        setShowLeftArrow(overflows && el.scrollLeft > 4);
        setShowRightArrow(overflows && el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    }, []);

    useEffect(() => {
        updateArrows();
        const el = categoriesRef.current;
        if (!el) return;
        el.addEventListener('scroll', updateArrows, { passive: true });
        window.addEventListener('resize', updateArrows);
        return () => {
            el.removeEventListener('scroll', updateArrows);
            window.removeEventListener('resize', updateArrows);
        };
    }, [updateArrows]);

    const scrollCategories = (direction) => {
        const el = categoriesRef.current;
        if (!el) return;
        const distance = Math.max(160, el.clientWidth * 0.7);
        el.scrollBy({ left: direction * distance, behavior: 'smooth' });
    };

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
        setExpandedChemicalName(null); // Reset expanded chemical when category changes
    };

    const toggleChemicalDetails = (chemicalName) => {
        setExpandedChemicalName(expandedChemicalName === chemicalName ? null : chemicalName); // Toggle details
    };

    return (
        <div className="products-page">
            <NavBar />

            <div className="content-area">
                {/* Hero Section */}
                <section className="hero-section">
                    <div className="hero-overlay">
                        <div className="container">
                            <h1>Enhance Your Textile Production</h1>
                            <p>Precision chemistry for modern textile manufacturing.</p>
                            <button className="consultation-button">Request a Consultation</button>
                        </div>
                    </div>
                </section>

                {/* Categories Section */}
                <section className="categories-section">
                    <div className="container">
                        <h2 className="section-title">Textile & Dyes Auxiliaries</h2>
                        <p className="section-description">
                            Textile auxiliaries are chemical formulations used for preparing fabric for dyeing. The dyeing auxiliaries further make the process smoother and help in achieving the desired outcomes.
                        </p>
                        <div className="categories-carousel">
                            <button
                                type="button"
                                aria-label="Scroll categories left"
                                className={`carousel-arrow carousel-arrow-left${showLeftArrow ? '' : ' is-hidden'}`}
                                onClick={() => scrollCategories(-1)}
                                tabIndex={showLeftArrow ? 0 : -1}
                            >
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
                            </button>
                            <div className="categories-buttons" ref={categoriesRef}>
                                {Object.keys(chemicalsByCategory).map((category) => (
                                    <button
                                        key={category}
                                        onClick={() => handleCategoryClick(category)}
                                        className={`category-button ${selectedCategory === category ? 'active' : ''}`}
                                    >
                                        {category}
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                aria-label="Scroll categories right"
                                className={`carousel-arrow carousel-arrow-right${showRightArrow ? '' : ' is-hidden'}`}
                                onClick={() => scrollCategories(1)}
                                tabIndex={showRightArrow ? 0 : -1}
                            >
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
                            </button>
                        </div>
                    </div>
                </section>

                {/* Main Content Section */}
                <section className="main-content-section">
                    <div className="container">
                        <div className="chemicals-grid">
                            {chemicalsByCategory[selectedCategory].map((chemical, index) => (
                                <div
                                    key={index}
                                    className={`chemical-tile ${expandedChemicalName === chemical.name ? 'expanded' : ''}`}
                                >
                                    <div
                                        className="chemical-header"
                                        onClick={() => toggleChemicalDetails(chemical.name)}
                                    >
                                        <img src={chemical.image} alt={chemical.name} className="chemical-image" />
                                        <h3>{chemical.name}</h3>
                                        <button className="expand-button">
                                            {expandedChemicalName === chemical.name ? '-' : '+'}
                                        </button>
                                    </div>

                                    {/* Details Section */}
                                    {expandedChemicalName === chemical.name && (
                                        <div className="chemical-details">
                                            <section className="detail-section">
                                                <h4 className="detail-heading">Description</h4>
                                                <p className="detail-body">{chemical.description}</p>
                                            </section>

                                            <section className="detail-section">
                                                <h4 className="detail-heading">Benefits</h4>
                                                <ul className="detail-bullets">
                                                    {chemical.benefits.map((benefit, i) => (
                                                        <li key={i}>{benefit}</li>
                                                    ))}
                                                </ul>
                                            </section>

                                            <section className="detail-section detail-tip">
                                                <h4 className="detail-heading">Expert Tip</h4>
                                                <p className="detail-body">{chemical.expertTip}</p>
                                            </section>

                                            {/* Quote Section */}
                                            {chemical.getQuoteDetails && (
                                                <div className="quote-section">
                                                    <h4 className="quote-title">Get a Quote</h4>
                                                    <ul className="quote-details-list">
                                                        <li><strong>Minimum Order Quantity</strong> <span>{chemical.getQuoteDetails.minimumOrderQuantity}</span></li>
                                                        <li><strong>Packaging Type</strong> <span>{chemical.getQuoteDetails.packagingType}</span></li>
                                                        <li><strong>Usage/Application</strong> <span>{chemical.getQuoteDetails.usage}</span></li>
                                                        <li><strong>Shelf Life</strong> <span>{chemical.getQuoteDetails.shelfLife}</span></li>
                                                        <li><strong>Form</strong> <span>{chemical.getQuoteDetails.form}</span></li>
                                                        <li><strong>Storage Instructions</strong> <span>{chemical.getQuoteDetails.storageInstructions}</span></li>
                                                    </ul>
                                                    <button className="request-quote-button">Request a Quote</button>
                                                </div>
                                            )}

                                            <a href={chemical.brochure} download className="download-link">
                                                <span className="download-icon" aria-hidden="true">
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v12m0 0l-5-5m5 5l5-5M4 20h16"/></svg>
                                                </span>
                                                Download Brochure
                                            </a>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </div>
    );
};

export default Products;
